import { Card, CardFooter } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useGraphConfig } from "@/store/contexts/GraphConfigContext";
import React, { useContext, useState } from "react";
import { Button } from "@/components/ui/button";
import { SendHorizontal, Loader2 } from "lucide-react";
import { osolutionClient as axios } from "@/services/axios";
import { GraphConfiguration, ProgressList } from "@/type/common";
import { ShineBorder } from "@/components/magicui/shine-border";
import { graphThemes } from "@/data/colors";
import AuthContext from "@/store/contexts/AuthContext";
import Progress from "./AiConfig/Progress";

const prepareConfigForAi = (config: GraphConfiguration) => {
  const sendedConfig = JSON.parse(JSON.stringify(config)) as any;
  delete sendedConfig.fields;
  if (config.type === "graph") {
    delete sendedConfig.table_options;
    delete sendedConfig.block_options;
  }
  if (config.type === "table") {
    delete sendedConfig.graph_options;
    delete sendedConfig.block_options;
  }
  if (config.type === "block") {
    delete sendedConfig.graph_options;
    delete sendedConfig.table_options;
  }
  return sendedConfig;
};

const cleanConfigAfterAi = (
  config: GraphConfiguration,
  updatedConfig: GraphConfiguration
) => {
  const newConfig = {
    ...config,
    ...updatedConfig,
  } as GraphConfiguration;

  newConfig.id = config.id;
  newConfig.createdAt = config.createdAt;
  newConfig.updatedAt = new Date().toISOString();

  if (!newConfig.graph_options.measures.some((m) => m.yAxis === "right")) {
    newConfig.graph_options.yAxis.right.showAxis = false;
  }

  if (!newConfig.graph_options.measures.some((m) => m.yAxis === "left")) {
    newConfig.graph_options.yAxis.left.showAxis = false;
  }

  if (newConfig.graph_options.theme === "custom") {
    if (newConfig.graph_options.colors.length !== 6) {
      newConfig.graph_options.colors = graphThemes.default;
      newConfig.graph_options.theme = "default";
    }
  } else if (newConfig.graph_options.theme) {
    newConfig.graph_options.colors =
      graphThemes[newConfig.graph_options.theme as keyof typeof graphThemes];
  }

  return newConfig;
};

const AiConfig = () => {
  const { config, setConfig, loadFields, step, setStep } = useGraphConfig();
  const { osolutionAuthData } = useContext(AuthContext);
  const [prompt, setPrompt] = useState<string>("");
  const [loading, setLoading] = useState(false);

  // Test progress items with different statuses
  const [progress, setProgress] = useState<ProgressList[]>([]);

  const handleConfigGeneration = async ({
    aiConfig,
    context_analysis,
    context,
  }: {
    aiConfig: any;
    context_analysis: any;
    context: any;
  }) => {
    if (!prompt.trim() || !config) return;
    setLoading(true);

    setProgress((p) =>
      p.map((p) => {
        if (p.step === "config-generation") {
          p.status = "running";
        }
        return p;
      })
    );

    let error = "";

    const updatedConfig = await axios
      .post(
        "/ai/generate-config",
        {
          prompt: prompt,
          config: config?.model === "" ? null : aiConfig,
          type: config?.type || "graph",
          authData: osolutionAuthData.current,
          context_analysis: context_analysis,
          context: context,
        },
        { timeout: 45000 }
      )
      .then((e) => e.data)
      .catch((e) => {
        error = e.response?.data?.reason || "error";
        return null;
      });

    if (updatedConfig) {
      setProgress((p) =>
        p.map((p) => {
          if (p.step === "config-generation") {
            p.status = "success";
            p.tokenCost = updatedConfig.consumption;
            p.tokenCostDescription = "Used to generate the config";
          }
          if (p.step === "graph-update") {
            p.status = "success";
          }
          return p;
        })
      );
      const newConfig = cleanConfigAfterAi(
        config,
        updatedConfig.result as GraphConfiguration
      );
      if (newConfig.model !== config?.model) {
        await loadFields(newConfig.model);
      }
      setConfig(newConfig);
      if (step === "model") {
        setStep("config");
      }
      setPrompt("");
    } else {
      setProgress((p) =>
        p.map((p) => {
          if (p.step === "config-generation") {
            p.status = "error";
            p.description =
              error === "credit"
                ? "You don't have enough credit to process this request."
                : "An error occured";
            p.retryAction = () =>
              handleConfigGeneration({ aiConfig, context_analysis, context });
          }
          return p;
        })
      );
    }
    setLoading(false);
  };

  const handleAiConfigGeneration = async ({
    aiConfig,
    context_analysis,
    context,
  }: {
    aiConfig: any;
    context_analysis: any;
    context: any;
  }) => {
    setLoading(true);

    setProgress((p) => [
      ...p.map((p) => {
        if (["model-selection"].includes(p.step)) {
          p.status = "success";
        }
        return p;
      }),
      {
        step: "config-generation",
        label: "Config Generation",
        description:
          "Generating the config based on the user input and selected model.",
        status: "pending",
      },
      {
        step: "graph-update",
        label: "Graph Update",
        description: "Updating the graph based on the new config.",
        status: "pending",
      },
    ]);
    handleConfigGeneration({ aiConfig, context_analysis, context });
  };

  const handleModelSelection = async ({
    aiConfig,
    context_analysis,
  }: {
    aiConfig: any;
    context_analysis: any;
  }) => {
    if (!prompt.trim() || !config) return;

    setLoading(true);

    setProgress((p) =>
      p.map((p) => {
        if (p.step === "model-selection") {
          p.status = "running";
        }
        return p;
      })
    );

    let error = "";

    const selectedModelsResponse = await axios
      .post(
        "/ai/generate-context",
        {
          prompt: prompt,
          config: config?.model === "" ? null : aiConfig,
          type: config?.type || "graph",
          authData: osolutionAuthData.current,
          context_analysis: context_analysis,
        },
        { timeout: 45000 }
      )
      .then((e) => e.data)
      .catch((_e) => {
        error = _e.response?.data?.reason || "error";
        return null;
      });

    if (
      selectedModelsResponse &&
      selectedModelsResponse.result.models.length > 0
    ) {
      const selectedModels = selectedModelsResponse.result;
      if (!selectedModels.need_sql_request) {
        setProgress((p) => [
          ...p.map((p) => {
            if (p.step === "model-selection") {
              p.status = "success";
              p.description = `Selected model: ${selectedModels.models[0].model}`;
              p.tokenCost = selectedModelsResponse.consumption;
              p.tokenCostDescription =
                "Used to select the model and generate the context";
            }
            return p;
          }),
          {
            step: "config-generation",
            label: "Config Generation",
            description:
              "Generating the config based on the user input and selected model.",
            status: "pending",
          },
          {
            step: "graph-update",
            label: "Graph Update",
            description: "Updating the graph based on the new config.",
            status: "pending",
          },
        ]);
        handleConfigGeneration({
          aiConfig: aiConfig,
          context_analysis: context_analysis,
          context: selectedModels,
        });
        return;
      } else {
        setProgress((p) => [
          ...p.map((p) => {
            if (p.step === "model-selection") {
              p.status = "need_validation";
              p.tokenCost = selectedModelsResponse.consumption;
              p.tokenCostDescription =
                "Used to select the model and generate the context";
              p.description = `It seems that you ask for a complex request that need a complete sql request. Will use ${selectedModels[
                "models"
              ]
                .map((m: any) => m.model)
                .join(", ")}.`;
              p.cancelAction = () => {
                setLoading(false);
                setProgress([]);
              };
              p.processAction = () => {
                handleAiConfigGeneration({
                  aiConfig: aiConfig,
                  context_analysis: context_analysis,
                  context: selectedModels,
                });
              };
            }
            return p;
          }),
        ]);
        return;
      }
    }
    setProgress((p) =>
      p.map((p) => {
        if (p.step === "model-selection") {
          p.status = "error";
          p.description =
            error === "credit"
              ? "You don't have enough credit to process this request."
              : "An error occured";
          p.retryAction = () =>
            handleModelSelection({ aiConfig, context_analysis });
        }
        return p;
      })
    );
  };

  const handlePromptAnalysis = async () => {
    if (!prompt.trim() || !config) return;
    setLoading(true);

    setProgress([
      {
        step: "prompt-analysis",
        label: "Prompt Analysis",
        description: "Analyzing the prompt",
        status: "running",
      },
    ]);
    setLoading(true);
    const aiConfig = prepareConfigForAi(config);
    let error = "";
    const analyzeResponse = await axios
      .post(
        "/ai/analyze-prompt",
        {
          prompt: prompt,
          config: config?.model === "" ? null : aiConfig,
          type: config?.type || "graph",
          authData: osolutionAuthData.current,
        },
        { timeout: 45000 }
      )
      .then((e) => e.data)
      .catch((e) => {
        error = e.response?.data?.reason || "error";
        return null;
      });
    if (analyzeResponse) {
      const analyze = analyzeResponse.result;
      const prompt_analysis: ProgressList = {
        step: "prompt-analysis",
        label: "Prompt Analysis",
        description: "Analyzing the prompt",
        status: "success",
        tokenCost: analyzeResponse.consumption,
        tokenCostDescription:
          "Used to analyze the prompt and determine the required tasks",
      };
      if (analyze.not_visualization_related) {
        setProgress([
          prompt_analysis,
          {
            step: "task-definition",
            label: "Task Definition",
            description:
              "The task is not related to data visualisation. It won't be processed.",
            status: "success",
          },
        ]);
        setPrompt("");
        setLoading(false);
      } else if (analyze.user_intention === "display_changes") {
        setProgress([
          prompt_analysis,
          {
            step: "task-definition",
            label: "Task Definition",
            description:
              "The user only ask for rendering update, no data update.",
            status: "success",
          },
          {
            step: "config-generation",
            label: "Config Generation",
            description: "Generating the config based on the user input.",
            status: "pending",
          },
          {
            step: "graph-update",
            label: "Graph Update",
            description: "Updating the graph based on the new config.",
            status: "pending",
          },
        ]);
        handleConfigGeneration({
          aiConfig: aiConfig,
          context_analysis: analyze,
          context: {},
        });
      } else {
        if (config.model !== "" && analyze.user_intention === "new_model") {
          setProgress([
            prompt_analysis,
            {
              step: "task-definition",
              label: "Task Definition",
              description:
                "The user ask for a visualization over a new model. It will override the current graph.",
              status: "need_validation",
              cancelAction: () => {
                setLoading(false);
                setProgress([]);
              },
              processAction: () => {
                setProgress((p) => [
                  ...p.map((p) => {
                    if (p.step === "task-definition") {
                      p.status = "success";
                    }
                    return p;
                  }),
                  {
                    step: "model-selection",
                    label: "Model Selection",
                    description: "Selecting the model",
                    status: "pending",
                  },
                ]);
                handleModelSelection({
                  aiConfig: aiConfig,
                  context_analysis: analyze,
                });
              },
            },
          ]);
        } else {
          setProgress((p) => [
            ...p.map((p) => {
              if (p.step === "prompt-analysis") {
                p.tokenCost = analyzeResponse.consumption;
                p.tokenCostDescription =
                  "Used to analyze the prompt and determine the required tasks";
              }
              if (["prompt-analysis", "task-definition"].includes(p.step)) {
                p.status = "success";
              }
              return p;
            }),
            {
              step: "model-selection",
              label: "Model Selection",
              description: "Selecting the model",
              status: "pending",
            },
          ]);
          handleModelSelection({
            aiConfig: aiConfig,
            context_analysis: analyze,
          });
        }
      }
    } else {
      setProgress((p) => [
        ...p.map((p) => {
          if (p.step === "prompt-analysis") {
            p.status = "error";
            p.description =
              error === "credit"
                ? "You don't have enough credit to process this request."
                : "An error occured";
            p.retryAction = () => handlePromptAnalysis();
          }
          return p;
        }),
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send the prompt when pressing Enter without holding Shift
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handlePromptAnalysis();
    }
  };

  return (
    <div className="p-4">
      <Card className="p-0 border overflow-hidden relative">
        <ShineBorder shineColor={["#A07CFE", "#FE8FB5", "#FFBE7B"]} />
        <Textarea
          className="border-none shadow-none focus-visible:ring-0 focus-visible:ring-transparent focus:outline-none resize-none min-h-[120px] p-3"
          placeholder="Ask AI to help you with your graph configuration..."
          value={prompt}
          disabled={loading}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <CardFooter className="flex justify-end !p-2 bg-muted/50 border-t">
          <Button
            onClick={handlePromptAnalysis}
            disabled={!prompt.trim() || loading}
            className="rounded-full"
            size="sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <SendHorizontal className="h-4 w-4 mr-2" />
                Configure
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
      {/* Progress component showing test steps */}
      {progress.length > 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-medium mb-2">Generation progress</h3>
          <Progress progress={progress} />
        </div>
      )}
    </div>
  );
};

export default AiConfig;
